import React, { useEffect, useState, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCloudUploadAlt,
  FaFileAlt,
  FaFolder,
  FaSearch,
  FaEdit,
  FaTimes,
  FaSchool,
  FaDownload,
  FaPlus,
  FaTrashAlt,
} from "react-icons/fa";
import { getSchools } from "../../services/schoolService";
import {
  getOperatorFiles,
  uploadOperatorFiles,
  updateOperatorFile,
  deleteOperatorDocument,
  deleteOperatorUpload,
} from "../../services/operatorService";
import { getLoggedInUser } from "../../utils/auth";
import "./OperatorLayer.css";

export const OperatorLayer = () => {
  const loggedUser = getLoggedInUser();
  const isOperator = loggedUser?.role?.toLowerCase() === "operator";
  const isAdmin = !isOperator;

  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Upload Modal State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadSchoolId, setUploadSchoolId] = useState("");
  const [fileName, setFileName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [updating, setUpdating] = useState(false);
  const editFileInputRef = useRef(null);

  useEffect(() => {
    loadSchools();
    loadFiles();
  }, []);

  useEffect(() => {
    loadFiles(selectedSchoolId);
  }, [selectedSchoolId]);

  const loadSchools = async () => {
    try {
      const res = await getSchools();
      if (res?.data) {
        setSchools(res.data);
      } else if (Array.isArray(res)) {
        setSchools(res);
      }
    } catch (error) {
      console.error("Failed to load schools:", error);
    }
  };

  const loadFiles = async (schoolId = null) => {
    setLoading(true);
    try {
      const res = await getOperatorFiles(schoolId || null);
      if (res?.status === "success" || res?.status === true) {
        setUploads(res.data || []);
      } else if (Array.isArray(res?.data)) {
        setUploads(res.data);
      } else if (Array.isArray(res)) {
        setUploads(res);
      } else {
        setUploads([]);
      }
    } catch (error) {
      console.error("Failed to load operator files:", error);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  };

  // Upload Form Handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadSchoolId) {
      toast.warn("Please select a school");
      return;
    }
    if (!fileName.trim()) {
      toast.warn("Please enter a file title/name");
      return;
    }
    if (selectedFiles.length === 0) {
      toast.warn("Please select at least one file to upload");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadOperatorFiles({
        school_id: uploadSchoolId,
        file_name: fileName,
        description: description,
        files: selectedFiles,
      });

      if (res?.status === "success" || res?.status === true) {
        toast.success(res.message || "Files uploaded successfully!");
        setShowUploadModal(false);
        setFileName("");
        setDescription("");
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        loadFiles(selectedSchoolId);
      } else {
        toast.error(res?.message || "Failed to upload files");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Upload error");
    } finally {
      setUploading(false);
    }
  };

  // Edit Handlers
  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setEditTitle(item.title || item.file_name || "");
    setEditDescription(item.description || "");
    setEditFile(null);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    setUpdating(true);
    try {
      const docId = editingItem.documents?.[0]?.document_id || editingItem.documents?.[0]?.id || null;
      const res = await updateOperatorFile({
        upload_id: editingItem.upload_id || editingItem.id,
        title: editTitle,
        description: editDescription,
        document_id: docId,
        document_title: editTitle,
        file: editFile || null,
      });

      if (res?.status === "success" || res?.status === true) {
        toast.success(res.message || "Updated successfully!");
        setShowEditModal(false);
        setEditingItem(null);
        loadFiles(selectedSchoolId);
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.message || "Update error");
    } finally {
      setUpdating(false);
    }
  };

  // Delete Handlers (Admin Only)
  const handleDeleteUpload = async (uploadId) => {
    if (!uploadId) return;
    if (!window.confirm("Are you sure you want to delete this upload?")) return;

    try {
      const res = await deleteOperatorUpload(uploadId);
      if (res?.status === "success" || res?.status === true) {
        toast.success(res.message || "Upload deleted successfully!");
        loadFiles(selectedSchoolId);
      } else {
        toast.error(res?.message || "Failed to delete upload");
      }
    } catch (error) {
      console.error("Delete upload error:", error);
      toast.error(error.response?.data?.message || "Delete error");
    }
  };

  const handleDeleteDoc = async (documentId) => {
    if (!documentId) return;
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      const res = await deleteOperatorDocument(documentId);
      if (res?.status === "success" || res?.status === true) {
        toast.success(res.message || "File deleted successfully!");
        loadFiles(selectedSchoolId);
      } else {
        toast.error(res?.message || "Failed to delete file");
      }
    } catch (error) {
      console.error("Delete file error:", error);
      toast.error(error.response?.data?.message || "Delete error");
    }
  };

  // Filter items by search
  const filteredUploads = uploads.filter((item) => {
    const term = search.toLowerCase();
    const title = (item.title || item.file_name || "").toLowerCase();
    const desc = (item.description || "").toLowerCase();
    const schoolName = (item.school_name || "").toLowerCase();
    return title.includes(term) || desc.includes(term) || schoolName.includes(term);
  });

  return (
    <div className="operator-container">
      <ToastContainer position="top-right" autoClose={2000} theme="colored" />

      {/* HEADER */}
      <div className="operator-header">
        <div className="operator-header-top">
          <div className="operator-brand">
            <div className="operator-brand-icon">
              <FaCloudUploadAlt />
            </div>
            <div className="operator-brand-text">
              <h4>Operator File Management</h4>
              <p>Upload and manage school ID cards and printed sheets</p>
            </div>
          </div>

          <div className="operator-actions">
            <button
              className="btn-upload-new"
              onClick={() => {
                setUploadSchoolId(selectedSchoolId || (schools[0]?.id || ""));
                setShowUploadModal(true);
              }}
            >
              <FaPlus /> Upload New File(s)
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="operator-stats-bar">
          <div className="operator-stat-item">
            <FaFolder />
            <span>Total Uploads: {uploads.length}</span>
          </div>
          <div className="operator-stat-item">
            <FaSchool />
            <span>Schools: {schools.length}</span>
          </div>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="operator-controls-card">
        <div className="operator-search-box">
          <FaSearch className="operator-search-icon" />
          <input
            type="text"
            placeholder="Search by title, description, school..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="operator-school-select"
          value={selectedSchoolId}
          onChange={(e) => setSelectedSchoolId(e.target.value)}
        >
          <option value="">All Schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* TABLE */}
      <div className="operator-table-card">
        {loading ? (
          <div className="empty-state-operator">
            <p>Loading files...</p>
          </div>
        ) : filteredUploads.length === 0 ? (
          <div className="empty-state-operator">
            <FaFileAlt />
            <h4>No Uploads Found</h4>
            <p>Upload your first batch of printed ID cards or document files.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="operator-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title & Description</th>
                  <th>School</th>
                  <th>Attached Files</th>
                  <th>Uploaded Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUploads.map((item, idx) => (
                  <tr key={item.id || item.upload_id || idx}>
                    <td>{idx + 1}</td>
                    <td>
                      <div className="file-title-cell">
                        <span>{item.title || item.file_name || "Untitled Upload"}</span>
                        {item.description && (
                          <span className="file-desc-sub">{item.description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="badge-school">
                        <FaSchool /> {item.school_name || `School #${item.school_id}`}
                      </span>
                    </td>
                    <td>
                      <div className="files-pill-list">
                        {item.documents && Array.isArray(item.documents) && item.documents.length > 0 ? (
                          item.documents.map((doc, dIdx) => (
                            <div key={doc.id || dIdx} className="file-pill-wrapper">
                              <a
                                href={doc.file_url || doc.url || doc.file_path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="file-download-pill"
                              >
                                <FaDownload /> {doc.title || `File ${dIdx + 1}`}
                              </a>
                              {isAdmin && (doc.id || doc.document_id) && (
                                <button
                                  type="button"
                                  className="btn-doc-delete"
                                  title="Delete this file"
                                  onClick={() => handleDeleteDoc(doc.id || doc.document_id)}
                                >
                                  <FaTrashAlt />
                                </button>
                              )}
                            </div>
                          ))
                        ) : item.file_url || item.file_path ? (
                          <a
                            href={item.file_url || item.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="file-download-pill"
                          >
                            <FaDownload /> View File
                          </a>
                        ) : (
                          <span className="text-muted small">No file link</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>
                        {item.created_at || item.uploaded_at || "N/A"}
                      </span>
                    </td>
                    <td>
                      <div className="operator-table-actions">
                        <button
                          className="btn-edit-action"
                          onClick={() => handleOpenEdit(item)}
                          title="Edit Upload Details"
                        >
                          <FaEdit /> Edit
                        </button>
                        {isAdmin && (
                          <button
                            className="btn-delete-action"
                            onClick={() => handleDeleteUpload(item.upload_id || item.id)}
                            title="Delete Upload"
                          >
                            <FaTrashAlt /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* UPLOAD MODAL */}
      {showUploadModal && (
        <div className="operator-modal-backdrop" onClick={() => setShowUploadModal(false)}>
          <div className="operator-modal" onClick={(e) => e.stopPropagation()}>
            <div className="operator-modal-header">
              <h5>Upload New File(s)</h5>
              <button className="btn-modal-close" onClick={() => setShowUploadModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleUploadSubmit}>
              <div className="operator-modal-body">
                <div className="form-field">
                  <label>Select School *</label>
                  <select
                    value={uploadSchoolId}
                    onChange={(e) => setUploadSchoolId(e.target.value)}
                    required
                  >
                    <option value="">-- Choose School --</option>
                    {schools.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label>File Title / Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Class 10 Ready ID Cards PDF"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Description</label>
                  <textarea
                    rows="3"
                    placeholder="e.g. Final printed ID cards sheet"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>Attach File(s) *</label>
                  <div
                    className="file-dropzone"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FaCloudUploadAlt className="dropzone-icon" />
                    <div className="dropzone-text">Click to choose file(s)</div>
                    <div className="dropzone-hint">Supports Single & Multiple Files (PDF, Excel, Images, ZIP)</div>
                  </div>
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />

                  {selectedFiles.length > 0 && (
                    <div className="selected-files-preview">
                      {selectedFiles.map((f, i) => (
                        <div key={i} className="selected-file-chip">
                          <span>{f.name} ({(f.size / 1024).toFixed(1)} KB)</span>
                          <FaTimes
                            style={{ cursor: "pointer", color: "#ef4444" }}
                            onClick={() => removeSelectedFile(i)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="operator-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={uploading}>
                  {uploading ? "Uploading..." : "Upload Files"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="operator-modal-backdrop" onClick={() => setShowEditModal(false)}>
          <div className="operator-modal" onClick={(e) => e.stopPropagation()}>
            <div className="operator-modal-header">
              <h5>Edit Upload Details</h5>
              <button className="btn-modal-close" onClick={() => setShowEditModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="operator-modal-body">
                <div className="form-field">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Description</label>
                  <textarea
                    rows="3"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>Replace File (Optional)</label>
                  <input
                    type="file"
                    ref={editFileInputRef}
                    onChange={(e) => setEditFile(e.target.files[0] || null)}
                  />
                  {editFile && (
                    <span className="small text-success mt-1 d-block">
                      Selected: {editFile.name}
                    </span>
                  )}
                </div>
              </div>

              <div className="operator-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={updating}>
                  {updating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorLayer;
